import { FC, createContext, useCallback, useContext, useMemo } from 'react';
import { METHOD_PATCH } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { toastService } from 'services';
import i18next from 'i18next';
import { ADD_SHIFT_AWARD } from 'constants/endpoints';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { queryRefetchService } from 'services/QueryRefetchService';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { useAwardTeamMemberViewFormContext } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.form.context';
import { useAwardShiftBlockDataContext } from 'modals/ShiftModal/src/views/AwardShiftBlockView/AwardShiftBlockView.data.context';

interface AwardShiftBlockFormContextType {
  onSubmit: () => void;
}

const AwardShiftBlockFormContext = createContext<AwardShiftBlockFormContextType>({
  onSubmit: () => {},
});

export const AwardShiftBlockFormProvider: FC = ({ children }) => {
  const { apiRequest } = useAPIActions();
  const { refreshBackgroundPage } = useShiftModalPropsContext();
  const { selectedJanes } = useAwardTeamMemberViewFormContext();
  const { leadShiftId, orders } = useAwardShiftBlockDataContext();

  const onSubmit = useCallback(() => {
    if (!leadShiftId) return;

    apiRequest(
      {
        __typename: 'LegacyAPI',
        method: METHOD_PATCH,
        url: ADD_SHIFT_AWARD(leadShiftId),
        body: {
          jane_id: selectedJanes.toString(),
        },
      },
      {
        onSuccess: async () => {
          toastService.success(i18next.t('common:SHIFT_BLOCK_SUCCESSFULLY_AWARDED'));
          refreshBackgroundPage();
          orders.forEach((order) => {
            queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${order.relayId}`);
          });
          shiftModalRoutingService.close();
        },
        onError: async () => {
          toastService.error(i18next.t('shift_modal:UNABLE_TO_AWARD_BLOCK'));
        },
      },
    );
  }, [apiRequest, leadShiftId, orders, selectedJanes, refreshBackgroundPage]);

  const value = useMemo(() => ({ onSubmit }), [onSubmit]);

  return (
    <AwardShiftBlockFormContext.Provider value={value}>
      {children}
    </AwardShiftBlockFormContext.Provider>
  );
};

export const useAwardShiftBlockFormContext = () => useContext(AwardShiftBlockFormContext);
